import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { 
  KeyRound, 
  Mail, 
  Lock, 
  User, 
  ArrowRight,
  Eye,
  EyeOff,
  Shield,
  CheckCircle,
  Loader2,
  Users,
  Star
} from 'lucide-react';

const Register = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    full_name: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    setIsVisible(true);
  }, []);

  useEffect(() => {
    // Calculate password strength
    const password = formData.password;
    let strength = 0;
    if (password.length >= 6) strength += 1;
    if (password.match(/[a-z]/) && password.match(/[A-Z]/)) strength += 1;
    if (password.match(/\d/)) strength += 1;
    if (password.match(/[^a-zA-Z\d]/)) strength += 1;
    setPasswordStrength(strength);
  }, [formData.password]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords don\'t match', {
        description: 'Please make sure both passwords are identical',
        duration: 4000,
      });
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password too weak', {
        description: 'Password must be at least 6 characters long',   
        duration: 4000,
      });
      return;
    }

    setLoading(true);

    try {
      const registerData = {
        email: formData.email,
        password: formData.password,
        full_name: formData.full_name
      };

      const response = await axios.post('/auth/register', registerData);
      const { access_token, user } = response.data;
      
      login(access_token, user);
      toast.success('Welcome to APT-AccessHub!', {
        description: `Hello ${user.full_name}, your account has been created successfully`,
        duration: 4000,
      });
      
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
      
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al registrarse';
      toast.error('Registration Error', {
        description: message,
        duration: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = formData.email && formData.password && formData.confirmPassword && 
                     formData.full_name && formData.password === formData.confirmPassword;

  const getPasswordStrengthColor = () => {
    switch(passwordStrength) {
      case 0: return 'bg-red-500';
      case 1: return 'bg-orange-500';
      case 2: return 'bg-yellow-500';
      case 3: return 'bg-blue-500';
      case 4: return 'bg-green-500';
      default: return 'bg-gray-300';
    }
  };

  const getPasswordStrengthText = () => {
    switch(passwordStrength) {
      case 0: return 'Very weak';
      case 1: return 'Weak';
      case 2: return 'Fair';
      case 3: return 'Strong';
      case 4: return 'Very strong';
      default: return '';
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-slate-900 via-purple-900 to-slate-800">
      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-600/20 via-blue-600/10 to-slate-800/30"></div>
      
      {/* Floating Elements */}
      <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-purple-400/10 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
      
      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className={`w-full max-w-md transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
          
          {/* Logo and Header */}
          <div className="text-center mb-8 space-y-4">
            <div className="inline-flex items-center justify-center p-4 rounded-2xl bg-gradient-to-br from-purple-500 to-blue-600 shadow-2xl shadow-purple-500/25 mb-6">
              <KeyRound className="h-12 w-12 text-white drop-shadow-lg" />
            </div>
            
            <div className="space-y-2">
              <h1 className="text-4xl font-bold bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent">
                APT-AccessHub
              </h1>
              <p className="text-purple-200/80 text-lg">
                Join the access revolution
              </p>
            </div>
            
            {/* Social Proof */}
            <div className="flex justify-center items-center space-x-4 mt-6">
              <div className="flex items-center space-x-2 text-purple-300/70">
                <Users className="h-4 w-4" />
                <span className="text-sm">+1000 users</span>
              </div>
              <div className="flex items-center space-x-2 text-purple-300/70">
                <Star className="h-4 w-4 fill-current" />
                <span className="text-sm">4.9/5 rating</span>
              </div>
            </div>
          </div>

          {/* Register Form */}
          <Card className="backdrop-blur-xl bg-white/10 border-white/20 shadow-2xl shadow-black/20">
            <CardHeader className="text-center pb-6">
              <CardTitle className="text-2xl font-semibold text-white">
                Create Account
              </CardTitle>
              <p className="text-purple-200/70 text-sm mt-2">
                Start managing your access intelligently
              </p>
            </CardHeader>
            
            <CardContent className="space-y-6">
              <form onSubmit={handleSubmit} className="space-y-5">
                
                {/* Full Name Field */}
                <div className="space-y-2">
                  <Label htmlFor="full_name" className="text-white/90 font-medium">
                    Full Name
                  </Label>
                  <div className="relative group">
                    <User className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 transition-colors duration-200 ${
                      focusedField === 'full_name' ? 'text-purple-400' : 'text-white/50'
                    }`} />
                    <Input
                      id="full_name"
                      name="full_name"
                      type="text"
                      value={formData.full_name}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('full_name')}
                      onBlur={() => setFocusedField(null)}
                      className="pl-10 h-12 bg-white/10 backdrop-blur-sm border-white/20 text-white placeholder:text-white/50 focus:border-purple-400 focus:ring-purple-400/20 transition-all duration-200"
                      placeholder="John Doe"
                      required
                    />
                    <div className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-purple-400 to-blue-500 transition-all duration-300 ${
                      focusedField === 'full_name' ? 'w-full' : 'w-0'
                    }`}></div>
                  </div>
                </div>

                {/* Email Field */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-white/90 font-medium">
                    Email Address
                  </Label>
                  <div className="relative group">
                    <Mail className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 transition-colors duration-200 ${
                      focusedField === 'email' ? 'text-purple-400' : 'text-white/50'
                    }`} />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('email')}
                      onBlur={() => setFocusedField(null)}
                      className="pl-10 h-12 bg-white/10 backdrop-blur-sm border-white/20 text-white placeholder:text-white/50 focus:border-purple-400 focus:ring-purple-400/20 transition-all duration-200"
                      placeholder="you@company.com"
                      required
                    />
                    <div className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-purple-400 to-blue-500 transition-all duration-300 ${
                      focusedField === 'email' ? 'w-full' : 'w-0'
                    }`}></div>
                  </div>
                </div>

                {/* Password Field */}
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-white/90 font-medium">
                    Password
                  </Label>
                  <div className="relative group">
                    <Lock className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 transition-colors duration-200 ${
                      focusedField === 'password' ? 'text-purple-400' : 'text-white/50'
                    }`} />
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('password')}
                      onBlur={() => setFocusedField(null)}
                      className="pl-10 pr-10 h-12 bg-white/10 backdrop-blur-sm border-white/20 text-white placeholder:text-white/50 focus:border-purple-400 focus:ring-purple-400/20 transition-all duration-200"
                      placeholder="••••••••"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/50 hover:text-white/80 transition-colors duration-200"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                    <div className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-purple-400 to-blue-500 transition-all duration-300 ${
                      focusedField === 'password' ? 'w-full' : 'w-0'
                    }`}></div>
                  </div>
                  
                  {/* Password Strength Indicator */}
                  {formData.password && (
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-white/60">Password strength</span>
                        <span className={`text-xs font-medium ${
                          passwordStrength >= 3 ? 'text-green-400' : 
                          passwordStrength >= 2 ? 'text-yellow-400' : 'text-red-400'
                        }`}>
                          {getPasswordStrengthText()}
                        </span>
                      </div>
                      <div className="flex space-x-1">
                        {[1,2,3,4].map((level) => (
                          <div 
                            key={level}
                            className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                              level <= passwordStrength ? getPasswordStrengthColor() : 'bg-white/20'
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Confirm Password Field */}
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-white/90 font-medium">
                    Confirm Password
                  </Label>
                  <div className="relative group">
                    <Lock className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 transition-colors duration-200 ${
                      focusedField === 'confirmPassword' ? 'text-purple-400' : 'text-white/50'
                    }`} />
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('confirmPassword')}
                      onBlur={() => setFocusedField(null)}
                      className="pl-10 pr-10 h-12 bg-white/10 backdrop-blur-sm border-white/20 text-white placeholder:text-white/50 focus:border-purple-400 focus:ring-purple-400/20 transition-all duration-200"
                      placeholder="••••••••"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/50 hover:text-white/80 transition-colors duration-200"
                    >
                      {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                    <div className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-purple-400 to-blue-500 transition-all duration-300 ${
                      focusedField === 'confirmPassword' ? 'w-full' : 'w-0'
                    }`}></div>
                  </div>
                  
                  {/* Password Match Indicator */}
                  {formData.confirmPassword && (
                    <div className="flex items-center space-x-2">
                      {formData.password === formData.confirmPassword ? (
                        <>
                          <CheckCircle className="h-4 w-4 text-green-400" />
                          <span className="text-xs text-green-400">Passwords match</span>
                        </>
                      ) : (
                        <>
                          <div className="h-4 w-4 rounded-full border-2 border-red-400"></div>
                          <span className="text-xs text-red-400">Passwords don't match</span>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={!isFormValid || loading}
                  className={`w-full h-12 bg-gradient-to-r from-purple-500 to-blue-600 hover:from-purple-600 hover:to-blue-700 text-white font-semibold rounded-xl shadow-lg transition-all duration-300 transform ${
                    !loading && isFormValid ? 'hover:scale-105 hover:shadow-xl hover:shadow-purple-500/25' : ''
                  } ${!isFormValid ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {loading ? (
                    <div className="flex items-center justify-center space-x-2">
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span>Creating account...</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center space-x-2">
                      <span>Create Free Account</span>
                      <ArrowRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-1" />
                    </div>
                  )}
                </Button>
              </form>

              {/* Divider */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/20"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-transparent text-white/60">Already have an account?</span>
                </div>
              </div>

              {/* Login Link */}
              <div className="text-center">
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center w-full h-11 bg-white/10 backdrop-blur-sm border border-white/20 text-white font-medium rounded-xl hover:bg-white/20 transition-all duration-300 space-x-2"
                >
                  <span>Sign In</span>
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* Trust Indicators */}
          <div className="mt-8 text-center">
            <div className="flex justify-center items-center space-x-4 text-purple-300/60 text-xs mb-4">
              <div className="flex items-center space-x-1">
                <Shield className="h-3 w-3" />
                <span>100% Secure</span>
              </div>
              <div className="w-1 h-1 bg-purple-300/40 rounded-full"></div>
              <div className="flex items-center space-x-1">
                <CheckCircle className="h-3 w-3" />
                <span>Free</span>
              </div>
              <div className="w-1 h-1 bg-purple-300/40 rounded-full"></div>
              <div className="flex items-center space-x-1">
                <Users className="h-3 w-3" />
                <span>Unlimited</span>
              </div>
            </div>
            
            {/* Copyright */}
            <div className="text-purple-300/50 text-xs">
              Powered by <span className="font-medium text-purple-300/70">Budget Digital Solutions LLC</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;